import {Alert, Badge, Group, Paper, Stack, Table, Text, Title} from "@mantine/core";
import {useQuery} from "@tanstack/react-query";
import {DataTable} from "mantine-datatable";
import {useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {ProductionDatacardAssignment, ProductionDatacardsResponse, ProductionEntityGroupRef} from "./types.ts";
import {getLocationLabel} from "./shared.ts";
import classes from "./ProductionModuleDatacardsPage.module.css";

interface DatacardLocationSummary {
    id: string;
    label: string;
    entityId: string;
    entityName: string;
    entityTypeName: string;
    groups: ProductionEntityGroupRef[];
    totalUses: number | null;
}

interface DatacardTypeSummary {
    id: string;
    cardTypeId: string;
    cardTypeName: string;
    assignmentCount: number;
    locationCount: number;
    totalUses: number | null;
    locations: DatacardLocationSummary[];
}

function formatUses(uses: number | null): string {
    if (uses === null || uses === undefined) {
        return "-";
    }
    if (uses < 0) {
        return "∞";
    }
    return String(uses);
}

function formatGroupNames(groups: ProductionEntityGroupRef[] | undefined): string {
    if (!groups || groups.length === 0) {
        return "-";
    }

    return groups
        .map((group) => group.name?.trim() ?? "")
        .filter(Boolean)
        .join(", ") || "-";
}

function groupAssignments(assignments: ProductionDatacardAssignment[]): DatacardTypeSummary[] {
    const byType = new Map<
        string,
        {
            summary: DatacardTypeSummary;
            locations: Map<string, DatacardLocationSummary>;
        }
    >();

    assignments.forEach((assignment) => {
        const typeKey = assignment.cardTypeId || assignment.cardTypeName || "unknown";
        if (!byType.has(typeKey)) {
            byType.set(typeKey, {
                summary: {
                    id: typeKey,
                    cardTypeId: assignment.cardTypeId,
                    cardTypeName: assignment.cardTypeName || assignment.cardTypeId || "-",
                    assignmentCount: 0,
                    locationCount: 0,
                    totalUses: 0,
                    locations: [],
                },
                locations: new Map<string, DatacardLocationSummary>(),
            });
        }

        const group = byType.get(typeKey);
        if (!group) {
            return;
        }

        group.summary.assignmentCount += 1;
        if (assignment.uses !== null && assignment.uses !== undefined) {
            if (assignment.uses < 0 || group.summary.totalUses === -1) {
                group.summary.totalUses = -1;
            } else {
                group.summary.totalUses = (group.summary.totalUses ?? 0) + assignment.uses;
            }
        } else if (group.summary.totalUses === 0) {
            group.summary.totalUses = null;
        }

        const locationKey = assignment.entityId;
        if (!group.locations.has(locationKey)) {
            group.locations.set(locationKey, {
                id: locationKey,
                label: getLocationLabel(assignment.location) || "-",
                entityId: assignment.entityId,
                entityName: assignment.entityName,
                entityTypeName: assignment.entityTypeName || "-",
                groups: assignment.groups ?? [],
                totalUses: 0,
            });
        }

        const location = group.locations.get(locationKey);
        if (!location) {
            return;
        }

        if (assignment.uses !== null && assignment.uses !== undefined) {
            if (assignment.uses < 0 || location.totalUses === -1) {
                location.totalUses = -1;
            } else {
                location.totalUses = (location.totalUses ?? 0) + assignment.uses;
            }
        } else if (location.totalUses === 0) {
            location.totalUses = null;
        }
    });

    return Array.from(byType.values())
        .map(({summary, locations}) => {
            const locationList = Array.from(locations.values()).sort((left, right) => left.entityName.localeCompare(right.entityName));
            return {
                ...summary,
                locationCount: locationList.length,
                locations: locationList,
            };
        })
        .sort((left, right) => left.cardTypeName.localeCompare(right.cardTypeName));
}

function ProductionModuleDatacardsPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const moduleInfo = useProductionModule();
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const query = useQuery<ProductionDatacardsResponse>({
        queryKey: ["prod-module", moduleId, "datacards"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/datacards`)).data as ProductionDatacardsResponse,
        enabled: !!moduleId,
    });

    const assignments = query.data?.datacards ?? [];
    const cardTypes = useMemo(() => groupAssignments(assignments), [assignments]);

    const uniqueLocations = useMemo(() => {
        const set = new Set<string>();
        assignments.forEach((row) => set.add(row.entityId));
        return set.size;
    }, [assignments]);

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }
    if (query.isPending) {
        return <LoaderScreen message="Loading datacards"/>;
    }
    if (query.isError || !query.data) {
        return <Page><Alert color="red" title="Datacards unavailable">Could not load datacard assignments.</Alert></Page>;
    }

    return (
        <Page>
            <title>{`${moduleInfo?.label || 'Production'} - Datacards`}</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Datacards</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                <Paper withBorder p="md">
                    <Group gap="xs">
                        <Badge color="blue" variant="light">Assignments: {assignments.length}</Badge>
                        <Badge color="teal" variant="light">Datacard Types: {cardTypes.length}</Badge>
                        <Badge color="gray" variant="light">Locations: {uniqueLocations}</Badge>
                    </Group>
                </Paper>

                <DataTable
                    records={cardTypes}
                    idAccessor="id"
                    columns={[
                        {
                            accessor: "cardTypeName",
                            title: "Datacard Type",
                            render: (row) => (
                                <div>
                                    <Text className={classes.typeName} size="sm">{row.cardTypeName || row.cardTypeId || "-"}</Text>
                                    <Text className={classes.typeHint} size="xs" c="dimmed">
                                        Click to view assigned producing entities
                                    </Text>
                                </div>
                            ),
                        },
                        {
                            accessor: "totalUses",
                            title: "Total Uses",
                            textAlign: "right",
                            render: (row) => formatUses(row.totalUses),
                        },
                        {
                            accessor: "locationCount",
                            title: "Locations",
                            textAlign: "right",
                        },
                    ]}
                    rowExpansion={{
                        expanded: {
                            recordIds: expandedRows,
                            onRecordIdsChange: (recordIds: unknown[]) => setExpandedRows(recordIds.map((recordId) => String(recordId))),
                        },
                        allowMultiple: true,
                        content: ({record}) => (
                            <div className={classes.expansion}>
                                <Table striped highlightOnHover withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Entity</Table.Th>
                                            <Table.Th>Type</Table.Th>
                                            <Table.Th>Group</Table.Th>
                                            <Table.Th>Location</Table.Th>
                                            <Table.Th style={{textAlign: "right"}}>Uses</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {record.locations.length > 0 ? record.locations.map((location) => (
                                            <Table.Tr key={location.id}>
                                                <Table.Td>
                                                    <Text size="sm">{location.entityName || location.entityId}</Text>
                                                    <Text size="xs" c="dimmed">{location.entityId}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{location.entityTypeName || "-"}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{formatGroupNames(location.groups)}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{location.label}</Text>
                                                </Table.Td>
                                                <Table.Td style={{textAlign: "right"}}>
                                                    <Text size="sm">{formatUses(location.totalUses)}</Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        )) : (
                                            <Table.Tr>
                                                <Table.Td colSpan={5}>
                                                    <Text size="sm" c="dimmed">No producing entities found for this datacard type.</Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        )}
                                    </Table.Tbody>
                                </Table>
                            </div>
                        ),
                    }}
                    rowClassName={classes.typeCell}
                    noRecordsText="No assigned datacards were found in tracked entities."
                />
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleDatacardsPage);
